import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ExpandMoreOutlined as ChevronIcon,
  AdminPanelSettingsOutlined as InternalUserIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import useBrand from '../../../styles/theme/brands/useBrand';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import ColoredUserAvatar from '../../../components/ColoredUserAvatar';
import ColorSchemeSelector from '../../../components/ColorSchemeSelector';
import useDashboardCompact from '../../../styles/theme/surfaces/useDashboardCompact';
import { loginUrl } from '../../../utils/loginRedirect';

// Altura do Toolbar do MUI: dense (48) no dashboard compacto, regular (64) acima
// de xl. A borda inferior soma 1px.
export const appBarHeight = (compact) => (compact ? 48 : 64);
export const APP_BAR_BORDER = 1;

function DashboardAppBar() {
  const { t } = useTranslation();
  const { user, collaborator, logout } = useAuth();
  const brand = useBrand();
  const compact = useDashboardCompact();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    // Ao entrar de novo, volta para o dashboard, não para o mobile.
    window.location.href = loginUrl('/dashboard/');
  };

  return (
    <React.Fragment>
      <AppBar
        position="fixed"
        elevation={0}
        // `dashboardAppBar` é o `primary` salvo quando a marca define outra cor; no
        // dark a AppBar fica na superfície escura. O useBrand escolhe o logo pelo fundo.
        color="dashboardAppBar"
        sx={{
          borderBottom: `${APP_BAR_BORDER}px solid`,
          borderColor: 'divider',
        }}
      >
        <Toolbar>

          <Box
            component="img"
            src={brand.dashboardAppBarLogo}
            alt={brand.name}
            // 24px no compacto (Toolbar dense, 48px); 32px no Toolbar regular.
            sx={{
              height: compact ? 24 : 32,
              width: 'auto',
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          {/* Internal (Beworks) users can manage every customer — make that
              unmistakable so nobody forgets which account they're in. */}
          {collaborator?.is_internal && (
            <Tooltip title={t('internalUser.tooltip')}>
              <Chip
                icon={<InternalUserIcon />}
                label={t('internalUser.label')}
                color="warning"
                size="small"
                sx={{ mr: 1.5 }}
              />
            </Tooltip>
          )}

          <Tooltip title={user?.name ?? ''}>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ gap: 1 }}>
              {/* 32px no compacto: com 40px o avatar estica o Toolbar dense além de 48px. */}
              <ColoredUserAvatar name={user?.name} sx={compact ? { width: 32, height: 32 } : undefined} />
            </IconButton>
          </Tooltip>

          {/* Popover, não Menu: é um painel de conta com controles (tema). No Menu o
              Tab fecha o menu e as setas só passam por MenuItem, então o toggle
              ficaria inacessível pelo teclado. */}
          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Stack direction="row" alignItems="center" spacing={2} px={2} py={1.5}>
              <ColoredUserAvatar name={user?.name} />
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="h6">
                  {user?.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {user?.email}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} px={2} py={1}>
              <Typography variant="body2" color="text.secondary">
                {t('colorScheme.label')}
              </Typography>
              <ColorSchemeSelector variant="toggle" />
            </Stack>
            <Divider />
            <List disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemText primary={t('logout')} />
              </ListItemButton>
            </List>
          </Popover>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
}

export default DashboardAppBar;
